import _ from "lodash";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { getSubjectsWithNoGradebook } from "GradingSetup/modules/Utils";

const _getCategorySets = ({ pointAssessmentSets, termId }) => {
  return _.filter(pointAssessmentSets, set => {
    const associatedEntities = _.get(set, "node.associatedEntities", []);
    return _.find(associatedEntities, entity => {
      const { id, __typename } = entity;
      return _.isEqual(__typename, "AcademicTerm") && _.isEqual(id, termId);
    });
  });
};

export const getCategorySets = _.memoize(_getCategorySets, params =>
  JSON.stringify(params)
);

const _getTermWiseCategorySetsCount = ({ rootNodes, termId }) => {
  let termCategorySetsCount = 0;
  const subjectsWithCategorySet = [];
  const subjectsWithNoCategorySet = [];
  _.forEach(rootNodes, node => {
    const subjectType = _.get(node, "type", "");
    if (!_.includes(getSubjectsWithNoGradebook(), subjectType)) {
      const pointAssessmentSets = _.get(node, "pointAssessmentSets.edges", []);
      const categorySets = getCategorySets({
        pointAssessmentSets,
        termId,
      });
      const categorySetsCount = _.size(categorySets);
      if (categorySetsCount > 0) {
        termCategorySetsCount += categorySetsCount;
        subjectsWithCategorySet.push({
          ...node,
          pointAssessmentSets: {
            edges: categorySets,
            totalCount: categorySetsCount,
          },
        });
      } else {
        subjectsWithNoCategorySet.push(node);
      }
    }
  });
  return {
    termCategorySetsCount,
    subjectsWithCategorySet,
    subjectsWithNoCategorySet,
  };
};

export const getTermWiseCategorySetsCount = _.memoize(
  _getTermWiseCategorySetsCount,
  params => JSON.stringify(params)
);

const _getEntitiesForCopySet = ({
  subjectId = null,
  primaryGradeId = null,
  gradeIds = [],
  termIds = [],
  subjectsWithCategorySet,
  subjectsWithNoCategorySet,
  grades,
  entityType,
  termId,
  modalState,
  rootNodes,
}) => {
  const entitiesForCopySet = [];
  if (subjectId && modalState === "DUPLICATE_TO_OTHER_GRADES") {
    const filteredPointAssessmentSets = getPointAssessmentSetsBySubjectIdAndTermId(
      { rootNodes, subjectId, termId }
    );

    _.forEach(gradeIds, gradeId => {
      const pointAssessmentSet = _.find(filteredPointAssessmentSets, set => {
        const associatedEntities = _.get(set, "node.associatedEntities", []);
        return (
          _.findIndex(associatedEntities, entity => {
            return (
              _.get(entity, "id") === gradeId &&
              _.get(entity, "__typename") === "Grade"
            );
          }) > -1
        );
      });

      const associatedEntities = [
        { id: gradeId, type: "GRADE" },
        { id: subjectId, type: "SUBJECT" },
        { id: termId, type: "ACADEMIC_TERM" },
      ];

      let entitiesObj = { associatedEntites: associatedEntities };

      if (pointAssessmentSet) {
        entitiesObj = {
          ...entitiesObj,
          overrideId: _.get(pointAssessmentSet, "node.id"),
        };
      }

      entitiesForCopySet.push(entitiesObj);
    });
  } else if (modalState === "DUPLICATE_TO_OTHER_TERMS") {
    _.forEach(termIds, termId => {
      entitiesForCopySet.push({
        associatedEntites: [
          { id: primaryGradeId, type: "GRADE" },
          { id: subjectId, type: "SUBJECT" },
          { id: termId, type: "ACADEMIC_TERM" },
        ],
      });
    });
  } else {
    const gradeIds = _.map(grades, grade => _.get(grade, "id"));

    _.forEach(subjectsWithNoCategorySet, subject => {
      const subjectId = _.get(subject, "id");
      const subjectType = _.get(subject, "type");
      if (subjectType === "TOK" || subjectType === "OTHERS") return;
      _.forEach(gradeIds, gradeId => {
        entitiesForCopySet.push({
          associatedEntites: [
            { id: gradeId, type: "GRADE" },
            { id: subjectId, type: "SUBJECT" },
            { id: termId, type: "ACADEMIC_TERM" },
          ],
        });
      });
    });
    _.forEach(subjectsWithCategorySet, subject => {
      const subjectId = _.get(subject, "id");
      const subjectType = _.get(subject, "type");
      if (subjectType === "TOK" || subjectType === "OTHERS") return;
      const pointAssessmentSets = _.get(
        subject,
        "pointAssessmentSets.edges",
        []
      );
      const gradeIdsAssociated = [];
      _.forEach(pointAssessmentSets, set => {
        const associatedEntities = _.get(set, "node.associatedEntities", []);
        _.forEach(associatedEntities, associatedEntity => {
          if (_.get(associatedEntity, "__typename", "") === entityType) {
            const associatedEntityId = _.get(associatedEntity, "id");
            if (_.includes(gradeIds, associatedEntityId)) {
              gradeIdsAssociated.push(associatedEntityId);
            }
          }
        });
      });
      const gradeIdsNotAssociated = _.difference(gradeIds, gradeIdsAssociated);
      _.forEach(gradeIdsNotAssociated, gradeId => {
        entitiesForCopySet.push({
          associatedEntites: [
            { id: gradeId, type: "GRADE" },
            { id: subjectId, type: "SUBJECT" },
            { id: termId, type: "ACADEMIC_TERM" },
          ],
        });
      });
    });
  }

  return entitiesForCopySet;
};

export const getEntitiesForCopySet = _.memoize(_getEntitiesForCopySet, params =>
  JSON.stringify(params)
);

const getPointAssessmentSetsBySubjectIdAndTermId = ({
  rootNodes,
  subjectId,
  termId,
}) => {
  const subjectPointAssessmentSets = _.get(
    _.find(rootNodes, rootNode => _.get(rootNode, "id") === subjectId),
    "pointAssessmentSets.edges",
    []
  );

  return _.filter(subjectPointAssessmentSets, set => {
    const associatedEntities = _.get(set, "node.associatedEntities", []);
    return (
      _.findIndex(associatedEntities, entity => {
        return (
          _.get(entity, "id") === termId &&
          _.get(entity, "__typename") === "AcademicTerm"
        );
      }) > -1
    );
  });
};

export const getAssociatedEntitiesForBulkImport = ({
  associatedEntities,
  targetTermId,
  rootNodes,
}) => {
  const entitiesForCopySet = [];
  _.forEach(associatedEntities, associatedEntity => {
    const { subjectId, gradeIds } = associatedEntity;

    const filteredPointAssessmentSets = getPointAssessmentSetsBySubjectIdAndTermId(
      { rootNodes, subjectId, termId: targetTermId }
    );

    _.forEach(gradeIds, gradeId => {
      const pointAssessmentSet = _.find(filteredPointAssessmentSets, set => {
        const associatedEntities = _.get(set, "node.associatedEntities", []);
        return (
          _.findIndex(associatedEntities, entity => {
            return (
              _.get(entity, "id") === gradeId &&
              _.get(entity, "__typename") === "Grade"
            );
          }) > -1
        );
      });

      const associatedEntities = [
        { id: gradeId, type: "GRADE" },
        { id: subjectId, type: "SUBJECT" },
        { id: targetTermId, type: "ACADEMIC_TERM" },
      ];

      let entitiesObj = { associatedEntites: associatedEntities };

      if (pointAssessmentSet) {
        entitiesObj = {
          ...entitiesObj,
          overrideId: _.get(pointAssessmentSet, "node.id"),
        };
      }

      entitiesForCopySet.push(entitiesObj);
    });
  });
  return entitiesForCopySet;
};

const _getAssessmentCategoriesChildId = ({ curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP:
      return "PYP_SCORE_CATEGORIES_V2";
    case CURRICULUM_TYPE_MYP:
      return "MYP_SCORE_CATEGORIES_V2";
    case CURRICULUM_TYPE_UBD:
      return "UBD_SCORE_CATEGORIES_V2";
    case CURRICULUM_TYPE_DP:
      return "DP_SCORE_CATEGORIES_V2";
  }
};

export const getAssessmentCategoriesChildId = _.memoize(
  _getAssessmentCategoriesChildId,
  params => JSON.stringify(params)
);
